import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useUserStore from '../store/userStore';
import './Components.css';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import Swal from 'sweetalert2';
import { authApi } from '../services/authApi';
import { useLang } from '../i18n/context';
import ShippingProfileForm from './ShippingProfileForm';
import './UserProfile.css';

const PROFILE_TABS = [
  { id: 'profile', label: 'profile.personalInfo' },
  { id: 'guide', label: 'profile.guideTab' },
  { id: 'shipping', label: 'profile.shipping.tab' },
];

const tabId = (id) => `user-profile-tab-${id}`;
const panelId = (id) => `user-profile-panel-${id}`;
const panelProps = (id) => ({
  id: panelId(id),
  role: 'tabpanel',
  'aria-labelledby': tabId(id),
});

function UserProfile() {
  const navigate = useNavigate();
  const { userInfo, token, updateUserInfo, logout, verifySession } = useUserStore();
  const { t } = useLang();
  const [editingInfo, setEditingInfo] = useState({
    ...userInfo,
    nickname: userInfo.nickname || '',
    binanceUID: userInfo.binanceUID || '',
    bitgetUID: userInfo.bitgetUID || '',
    wechat: userInfo.wechat || '',
    okxUID: userInfo.okxUID || ''
  });
  const [refreshing, setRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState('profile');
  const [saving, setSaving] = useState(false);
  const tabRefs = useRef([]);

  useEffect(() => {
    setEditingInfo({
      ...userInfo,
      nickname: userInfo.nickname || '',
      binanceUID: userInfo.binanceUID || '',
      bitgetUID: userInfo.bitgetUID || '',
      wechat: userInfo.wechat || '',
      okxUID: userInfo.okxUID || ''
    });
  }, [userInfo]);

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      const result = await authApi.updateProfile({
        token,
        nickname: editingInfo.nickname,
        binanceUID: editingInfo.binanceUID || null,
        bitgetUID: editingInfo.bitgetUID || null,
        wechat: editingInfo.wechat || null,
        okxUID: editingInfo.okxUID || null,
      });

      if (!result.success) throw new Error(t('profile.saveFailed'));

      updateUserInfo(editingInfo);
      Swal.fire({
        icon: 'success',
        title: t('profile.saveSuccess'),
        background: '#1e222d',
        color: '#d1d4dc',
        confirmButtonColor: '#bfa14a',
        confirmButtonText: t('profile.confirm'),
        timer: 1500,
        timerProgressBar: true,
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: t('profile.saveFailed'),
        text: err?.message || t('profile.retryLater'),
        background: '#1e222d',
        color: '#d1d4dc',
        confirmButtonColor: '#ef5350',
        confirmButtonText: t('profile.confirm')
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRefreshAll = async () => {
    try {
      setRefreshing(true);
      if (userInfo.email) {
        await verifySession();
      }
      Swal.fire({
        icon: 'success',
        title: t('profile.refreshed'),
        background: '#1e222d',
        color: '#d1d4dc',
        timer: 1200,
        timerProgressBar: true,
        toast: true,
        position: 'top-end',
        showConfirmButton: false
      });
    } catch {
      Swal.fire({
        icon: 'error',
        title: t('profile.refreshFailed'),
        background: '#1e222d',
        color: '#d1d4dc',
        confirmButtonColor: '#ef5350',
        confirmButtonText: t('profile.confirm')
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleLogout = () => {
    Swal.fire({
      title: t('profile.confirmLogout'),
      icon: 'warning',
      background: '#1e222d',
      color: '#d1d4dc',
      showCancelButton: true,
      confirmButtonColor: '#ef5350',
      cancelButtonColor: '#666',
      confirmButtonText: t('profile.logout'),
      cancelButtonText: t('profile.cancel'),
    }).then((result) => {
      if (result.isConfirmed) {
        logout();
        navigate('/');
      }
    });
  };

  const copyInviteCode = () => {
    navigator.clipboard.writeText(userInfo.myInviteCode);
    Swal.fire({
      icon: 'success',
      title: t('profile.copied'),
      background: '#1e222d',
      color: '#d1d4dc',
      timer: 1000,
      toast: true,
      position: 'top-end',
      showConfirmButton: false
    });
  };

  const isPremium = userInfo.level === t('profile.advancedUser') || userInfo.user_type === 'premium';

  const handleTabKeyDown = (event, currentIndex) => {
    let nextIndex;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % PROFILE_TABS.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + PROFILE_TABS.length) % PROFILE_TABS.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = PROFILE_TABS.length - 1;
    if (nextIndex === undefined) return;

    event.preventDefault();
    setCurrentPage(PROFILE_TABS[nextIndex].id);
    tabRefs.current[nextIndex]?.focus();
  };

  return (
    <SimpleBar className="user-profile-page">
      <main className="user-profile-shell">

        {/* 信息卡片 */}
        <section className="user-profile-card user-profile-summary">
          <div className="user-profile-summary-row">
            <div className="user-profile-avatar" aria-hidden="true">{userInfo.avatar || '👤'}</div>
            <div className="user-profile-identity">
              <h1>{userInfo.username}</h1>
              <div className="user-profile-meta">
                <span className="user-profile-level">{userInfo.level}</span>
                <span>·</span>
                <span className={userInfo.is_verified ? 'is-verified' : 'is-unverified'}>
                  {userInfo.is_verified ? t('profile.verified') : t('profile.unverified')}
                </span>
                {isPremium && (
                  <>
                    <span>·</span>
                    <span className="user-profile-membership">{t('profile.memberDays').replace('{days}', userInfo.premium_days || 0)}</span>
                  </>
                )}
              </div>
              <div className="user-profile-invite">
                <span>{t('profile.inviteCode')}</span>
                <code>{userInfo.myInviteCode}</code>
                <button type="button" className="user-profile-copy" onClick={copyInviteCode}>{t('profile.copy')}</button>
              </div>
            </div>
            <button type="button" className="user-profile-refresh" onClick={handleRefreshAll} disabled={refreshing}>
              {refreshing ? t('profile.refreshing') : t('profile.refreshInfo')}
            </button>
          </div>
        </section>

        {/* Tab 切换 */}
        <div className="user-profile-tabs" role="tablist">
          {PROFILE_TABS.map((tab, index) => {
            const isActive = currentPage === tab.id;
            return (
              <button
                key={tab.id}
                ref={(element) => { tabRefs.current[index] = element; }}
                id={tabId(tab.id)}
                type="button"
                role="tab"
                aria-controls={panelId(tab.id)}
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                className={isActive ? 'is-active' : ''}
                onClick={() => setCurrentPage(tab.id)}
                onKeyDown={(event) => handleTabKeyDown(event, index)}
              >
                {t(tab.label)}
              </button>
            );
          })}
        </div>

        {/* 个人信息表单 */}
        <section {...panelProps('profile')} className="user-profile-card" hidden={currentPage !== 'profile'}>
          {currentPage === 'profile' && (
            <>
              <div className="user-profile-fields">
                <FormRow id="profile-nickname" label={t('profile.nickname')} value={editingInfo.nickname} onChange={v => setEditingInfo({...editingInfo, nickname: v})} placeholder={t('profile.nicknamePlaceholder')} />
                <FormRow id="profile-email" label={t('profile.email')} value={userInfo.email} disabled />
                <FormRow id="profile-binance-uid" label={t('profile.binanceUID')} value={editingInfo.binanceUID || ''} onChange={v => setEditingInfo({...editingInfo, binanceUID: v})} placeholder={t('profile.binanceUIDPlaceholder')} hint={t('profile.binanceUIDHint')} />
                <FormRow id="profile-bitget-uid" label={t('profile.bitgetUID')} value={editingInfo.bitgetUID || ''} onChange={v => setEditingInfo({...editingInfo, bitgetUID: v})} placeholder={t('profile.bitgetUIDPlaceholder')} hint={t('profile.bitgetUIDHint')} />
                <FormRow id="profile-wechat" label={t('profile.wechat')} value={editingInfo.wechat || ''} onChange={v => setEditingInfo({...editingInfo, wechat: v})} placeholder={t('profile.wechatPlaceholder')} hint={t('profile.wechatHint')} />
                <FormRow id="profile-okx-uid" label={t('profile.okxUID')} value={editingInfo.okxUID || ''} onChange={v => setEditingInfo({...editingInfo, okxUID: v})} placeholder={t('profile.okxUIDPlaceholder')} hint={t('profile.okxUIDHint')} />
                <FormRow id="profile-parent-invite" label={t('profile.parentInviteCode')} value={userInfo.parentInviteCode || t('profile.none')} disabled hint={t('profile.parentCodeHint')} />
              </div>
              <div className="user-profile-actions">
                <button type="button" className="user-profile-save" onClick={handleSaveProfile} disabled={saving}>{saving ? t('profile.saving') : t('profile.save')}</button>
                <button type="button" className="user-profile-logout" onClick={handleLogout}>{t('profile.logoutBtn')}</button>
              </div>
            </>
          )}
        </section>

        {/* 完善指引 */}
        <section {...panelProps('guide')} className="user-profile-card user-profile-guide" hidden={currentPage !== 'guide'}>
          {currentPage === 'guide' && (
            <>
              <h2>{t('profile.guideTitle')}</h2>

              <GuideSection title={t('profile.guide1Title')}>
                <p>{t('profile.guide1Nickname')}</p>
                <p>{t('profile.guide1Email')}</p>
              </GuideSection>

              <GuideSection title={t('profile.guide2Title')}>
                <p>{t('profile.guide2Binance')}</p>
                <GuideLink href="https://www.bsmkweb.cc/register?ref=YL188" code="YL188" t={t} />
                <p>{t('profile.guide2Bitget')}</p>
                <GuideLink href="https://partner.zysh.xyz/bg/9zp7231q" code="YL188" t={t} />
                <p>{t('profile.guide2Okx')}</p>
                <GuideLink href="https://www.promoohivex.com/join/YL188" code="YL188" t={t} />
              </GuideSection>

              <GuideSection title={t('profile.guide3Title')}>
                <p>{t('profile.guide3Parent')}</p>
                <p>{t('profile.guide3Mine')}</p>
              </GuideSection>

              <GuideSection title={t('profile.guide4Title')}>
                <p>{t('profile.guide4Note1')}</p>
                <p>{t('profile.guide4Note2')}</p>
                <p>{t('profile.guide4Note3')}</p>
              </GuideSection>
            </>
          )}
        </section>

        <div {...panelProps('shipping')} hidden={currentPage !== 'shipping'}>
          {currentPage === 'shipping' && <ShippingProfileForm token={token} />}
        </div>
      </main>
    </SimpleBar>
  );
}

function FormRow({ id, label, value, onChange, placeholder, disabled, hint }) {
  return (
    <div className="user-profile-field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="text"
        value={value || ''}
        onChange={onChange ? e => onChange(e.target.value) : undefined}
        placeholder={placeholder}
        disabled={disabled}
      />
      {hint && <small>{hint}</small>}
    </div>
  );
}

function GuideSection({ title, children }) {
  return (
    <div className="user-profile-guide-section">
      <h3>{title}</h3>
      {children}
    </div>
  );
}

function GuideLink({ href, code, t }) {
  return (
    <div className="user-profile-guide-link">
      <a href={href} target="_blank" rel="noopener noreferrer">
        {href}
      </a>
      <span>
        {t('profile.inviteCodeLabel')} <strong>{code}</strong>
      </span>
    </div>
  );
}

export default UserProfile;
